import React from "react";
import { TouchableOpacity, View } from "react-native";
import RacecardAndResultWidgetHeader from "./shared/RacecardAndResultWidgetHeader";
import RacecardAndResultWidgetContainer from "./shared/RacecardAndResultWidgetContainer";
import RacecardAndResultWidgetRaceDescription from "./shared/RacecardAndResultWidgetRaceDescription";
import RacecardAndResultWidgetRunnerContainer from "./shared/RacecardAndResultWidgetRunnerContainer";
import RacecardAndResultWidgetToteReturns from "./shared/RacecardAndResultWidgetToteReturns";
import {
  RacecardRes,
  ResultRes,
} from "../../../../types/races/racecardAndResultWidget";
import usePressableProps from "../../../../hooks/usePressableProps";
import { prepareParams } from "../../../Screens/Results/utils";
import { logEvent } from "../../../../client/analytics/trackPageView";

/**
 * RacecardAndResultWidgetDetailed displays a detailed racecard or result widget
 *
 * This renders on wider screens - the breakpoint is set in RacecardAndResultWidget
 *
 * @property {RacecardRes | ResultRes} data - The racecard or result data to display
 * @property {boolean} [isRacecard] - Indicates if the data is for a racecard (default: false, meaning it's a result)
 *
 * @returns {JSX.Element} The RacecardAndResultWidgetDetailed component
 */

type Props = {
  data: RacecardRes | ResultRes;
  isRacecard?: boolean;
};

const RacecardAndResultWidgetDetailed = ({ data, isRacecard }: Props) => {
  const isResult = !isRacecard;

  const pressableProps = usePressableProps({
    screen: isRacecard ? "IndividualRacecard" : "IndividualResult",
    params: prepareParams({
      date: data?.meeting?.date,
      track: data?.meeting?.track?.slug,
      ...(isRacecard
        ? { startTime: data?.start_time_scheduled }
        : { time: data?.start_time_scheduled }),
    }),
  });

  return (
    <TouchableOpacity
      {...pressableProps}
      onPress={() =>
        logEvent({
          key: "single-race-widget-click",
          data: {},
        })
      }
    >
      <RacecardAndResultWidgetContainer isResult={isResult} isDetailed>
        <View
          style={{ flex: 1, marginRight: 24, justifyContent: "space-between" }}
        >
          <View>
            <RacecardAndResultWidgetHeader
              data={data}
              isRacecard={isRacecard}
            />
            <RacecardAndResultWidgetRaceDescription
              data={data}
              isResult={isResult}
            />
          </View>
          {isResult && "totes" in data && (
            <RacecardAndResultWidgetToteReturns totes={data.totes} />
          )}
        </View>
        <View style={{ flex: 2, minWidth: "auto", maxWidth: "auto" }}>
          <RacecardAndResultWidgetRunnerContainer
            data={data}
            isDetailed
            isRacecard={isRacecard}
          />
        </View>
      </RacecardAndResultWidgetContainer>
    </TouchableOpacity>
  );
};

export default RacecardAndResultWidgetDetailed;
